import { useCallback, useEffect, useRef, useState } from "react";
import { AnswerCard } from "./components/AnswerCard";
import { Header } from "./components/Header";
import { Scoreboard } from "./components/Scoreboard";
import { StatChips } from "./components/StatChips";
import { Waiting } from "./components/Spinner";
import { Lobby } from "./screens/Lobby";
import { DuelResult } from "./screens/DuelResult";
import { SignIn } from "./screens/SignIn";
import { isCorrect } from "../shared/norm";
import { answer, buildQueue, counts, nowSeconds, requeue, reviewGap, type Box, type Boxes } from "../shared/srs";
import { DECK_IDS, DEFAULT_DECK, getDeck, isDeckId, type DeckId, type Entry } from "../shared/vocab";
import { isRoomCode, type WordCount } from "../shared/protocol";
import { useDuel } from "./lib/useDuel";
import * as api from "./lib/api";
import * as store from "./lib/storage";
import { speak, speechSupported, stopSpeaking } from "./lib/speech";

type Screen = "solo" | "lobby" | "battle" | "end" | "login";
type Phase = "answer" | "revealed";

/**
 * Correct answers advance on their own after this long. 0 disables it.
 *
 * Disabled: 900 ms was not enough to read the accepted alternatives or hear
 * the pronunciation, so a right answer taught less than a wrong one. The
 * learner now leaves the card with Enter or Next, right or wrong.
 */
const AUTO_ADVANCE_MS = 0;
/** How long unsent progress sits before being flushed to the server. */
const FLUSH_DELAY_MS = 2000;

export function App() {
  const [screen, setScreen] = useState<Screen>("solo");
  const [deckId, setDeckId] = useState<DeckId>(() => {
    const stored = store.storedDeck();
    return isDeckId(stored) ? stored : DEFAULT_DECK;
  });
  const [user, setUser] = useState("");

  const [boxes, setBoxes] = useState<Boxes>({});
  const [queue, setQueue] = useState<Entry[]>([]);
  const [qpos, setQpos] = useState(0);
  const [streak, setStreak] = useState(0);

  const [phase, setPhase] = useState<Phase>("answer");
  const [typed, setTyped] = useState("");
  const [wasCorrect, setWasCorrect] = useState<boolean | null>(null);

  const [name, setName] = useState(() => store.storedName());
  const [wordCount, setWordCount] = useState<WordCount>(10);
  const [joinCode, setJoinCode] = useState("");
  const [joinError, setJoinError] = useState("");
  const [muted, setMuted] = useState(() => store.storedMuted());
  const [canSpeak] = useState(speechSupported);

  const { duel, create, join, leave, scoreAnswer, advance, rematch, clearError } = useDuel();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const advanceTimer = useRef<number | undefined>(undefined);
  const flushTimer = useRef<number | undefined>(undefined);
  const pending = useRef<Boxes>({});
  /** The keypress the answer input has already acted on. See the Enter effect. */
  const consumedEnter = useRef<KeyboardEvent | null>(null);

  const deck = getDeck(deckId);
  const battling = screen === "battle";
  const current: Entry | undefined = battling ? duel.deck[duel.bidx] : queue[qpos];
  /** The deck whose cards are on screen: the duel's during a duel, else the switcher's. */
  const activeDeck = () =>
    latest.current.screen === "battle" ? getDeck(duel.deckId) : getDeck(latest.current.deckId);

  // Callbacks that run outside the render cycle (timers, document listeners)
  // read from here rather than from a captured closure.
  const latest = useRef({ screen, phase, queue, qpos, boxes, deckId, user, muted });
  latest.current = { screen, phase, queue, qpos, boxes, deckId, user, muted };

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el && !el.disabled) {
        el.focus();
        return;
      }
      // While an answer is revealed the input is disabled and cannot take
      // focus. Park focus on the document instead of leaving it on whatever
      // button was just clicked, or the next Enter re-activates that button
      // rather than advancing the card.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) active.blur();
    });
  }, []);

  /* ── progress persistence ─────────────────────────────────────────── */

  const flush = useCallback(async () => {
    const changes = pending.current;
    const target = latest.current.deckId;
    pending.current = {};
    if (!latest.current.user || Object.keys(changes).length === 0) return;
    try {
      await api.pushProgress(target, changes);
    } catch {
      // Keep them for the next flush rather than losing the answers.
      pending.current = { ...changes, ...pending.current };
    }
  }, []);

  const scheduleFlush = useCallback(
    (word: string, box: Box) => {
      if (!latest.current.user) return; // guests stay entirely local
      pending.current[word] = box;
      clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => void flush(), FLUSH_DELAY_MS) as unknown as number;
    },
    [flush],
  );

  useEffect(() => {
    const onHide = () => {
      clearTimeout(flushTimer.current);
      api.beaconProgress(latest.current.deckId, pending.current);
      pending.current = {};
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  /* ── deck loading ─────────────────────────────────────────────────── */

  const openDeck = useCallback(
    async (id: DeckId, forUser: string) => {
      let next: Boxes = store.loadBoxes(id, forUser);
      if (forUser) {
        try {
          next = await api.fetchProgress(id);
        } catch {
          // Offline or signed out mid-flight: the local copy still works.
        }
      }
      setBoxes(next);
      setQueue(buildQueue(getDeck(id).entries, next, nowSeconds()));
      setQpos(0);
      setPhase("answer");
      setTyped("");
      setWasCorrect(null);
      focusInput();
    },
    [focusInput],
  );

  // The cookie, not localStorage, decides whether we are signed in.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const email = await api.fetchMe();
      if (cancelled) return;
      store.setStoredUser(email);
      setUser(email);
      await openDeck(deckId, email);
    })();
    return () => {
      cancelled = true;
    };
    // Runs once: later deck changes go through selectDeck.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDeck = useCallback(
    (id: DeckId) => {
      if (id === latest.current.deckId) return;
      clearTimeout(advanceTimer.current);
      void flush();
      store.setStoredDeck(id);
      setDeckId(id);
      setStreak(0);
      // In practice this restarts the queue in the new deck; in the lobby it
      // just swaps which deck the duel to come will use.
      void openDeck(id, latest.current.user);
    },
    [flush, openDeck],
  );

  /* ── answering ────────────────────────────────────────────────────── */

  const next = useCallback(() => {
    clearTimeout(advanceTimer.current);
    const s = latest.current;
    if (s.phase !== "revealed") return;

    if (s.screen === "battle") {
      advance();
      setPhase("answer");
      setTyped("");
      setWasCorrect(null);
      focusInput();
      return;
    }

    let q = s.queue;
    let p = s.qpos + 1;
    if (p >= q.length) {
      q = buildQueue(getDeck(s.deckId).entries, s.boxes, nowSeconds());
      p = 0;
      setQueue(q);
    }
    setQpos(p);
    setPhase("answer");
    setTyped("");
    setWasCorrect(null);
    focusInput();
  }, [advance, focusInput]);

  const applyResult = useCallback(
    (ok: boolean, entry: Entry) => {
      if (latest.current.screen === "battle") {
        scoreAnswer(ok);
      } else {
        const prev = latest.current.boxes[entry.w];
        const box = answer(prev, ok, nowSeconds());
        // A right answer before the word is due changes nothing: nothing to
        // save, nothing to flush, and the word waits for its next lap.
        if (box !== prev) {
          const updated = { ...latest.current.boxes, [entry.w]: box };
          setBoxes(updated);
          store.saveBoxes(latest.current.deckId, latest.current.user, updated);
          scheduleFlush(entry.w, box);
        }
        const gap = reviewGap(prev, box);
        if (gap !== null) setQueue((q) => requeue(q, latest.current.qpos, entry, gap));
        setStreak((s) => (ok ? s + 1 : 0));
      }

      setPhase("revealed");
      setWasCorrect(ok);

      // Spoken here, synchronously inside the key or click handler that
      // revealed the answer — iOS refuses speech started from a timer.
      if (canSpeak && !latest.current.muted) speak(entry.a[0] ?? "", activeDeck().answerLang);

      if (ok && AUTO_ADVANCE_MS > 0) {
        clearTimeout(advanceTimer.current);
        advanceTimer.current = setTimeout(() => next(), AUTO_ADVANCE_MS) as unknown as number;
      }
    },
    [canSpeak, next, scheduleFlush, scoreAnswer],
  );

  const check = useCallback(
    (nativeEvent?: KeyboardEvent) => {
      if (nativeEvent) consumedEnter.current = nativeEvent;
      if (latest.current.phase !== "answer" || !typed.trim() || !current) return;
      applyResult(isCorrect(typed, current.a), current);
    },
    [applyResult, current, typed],
  );

  const giveUp = useCallback(() => {
    if (latest.current.phase !== "answer" || !current) return;
    applyResult(false, current);
  }, [applyResult, current]);

  // Enter advances once the answer is revealed. The input is disabled then, so
  // the listener has to live on the document.
  //
  // The guard is not optional. React flushes a discrete event's state updates
  // synchronously when its own handler returns, which happens *before* the
  // native event finishes bubbling up to document. Without it, the single
  // keypress that revealed the answer would immediately advance past it, and
  // the learner would never see what the answer was.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      if (consumedEnter.current === e) return; // the input already used this keypress
      if (latest.current.phase === "revealed") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [next]);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  /* ── duel plumbing ────────────────────────────────────────────────── */

  useEffect(() => {
    if (duel.phase === "playing") {
      setScreen("battle");
      setPhase("answer");
      setTyped("");
      setWasCorrect(null);
      focusInput();
    } else if (duel.phase === "over") {
      clearTimeout(advanceTimer.current);
      setScreen("end");
    }
  }, [duel.phase, focusInput]);

  const displayName = () =>
    name.trim() || (user ? user.split("@")[0]! : "Player");

  const startDuel = () => {
    setJoinError("");
    void create(deckId, wordCount, displayName());
  };

  const joinDuel = () => {
    const code = joinCode.trim().toUpperCase();
    if (!isRoomCode(code)) {
      setJoinError("Enter the 4-letter code.");
      return;
    }
    setJoinError("");
    join(code, displayName(), deckId);
  };

  const leaveDuel = () => {
    clearTimeout(advanceTimer.current);
    leave();
    setJoinCode("");
    setJoinError("");
    setScreen("solo");
    setPhase("answer");
    setTyped("");
    setWasCorrect(null);
    focusInput();
  };

  /* ── account ──────────────────────────────────────────────────────── */

  const onSignedIn = async (email: string) => {
    store.setStoredUser(email);
    setUser(email);

    // Adopt guest progress, which the server keeps only for decks where the
    // account has no history of its own.
    const guest: Partial<Record<DeckId, Boxes>> = {};
    for (const id of DECK_IDS) {
      const b = store.loadBoxes(id, "");
      if (Object.keys(b).length > 0) guest[id] = b;
    }
    if (Object.keys(guest).length > 0) {
      try {
        await api.migrateProgress(guest);
      } catch {
        // Not fatal: the account simply starts empty.
      }
    }

    setScreen("solo");
    await openDeck(latest.current.deckId, email);
  };

  const onSignOut = async () => {
    await flush();
    try {
      await api.signOut();
    } catch {
      // The cookie is cleared server-side on the next request anyway.
    }
    store.setStoredUser("");
    setUser("");
    setScreen("solo");
    await openDeck(latest.current.deckId, "");
  };

  /* ── render ───────────────────────────────────────────────────────── */

  const stats = counts(deck.entries, boxes);
  const alts = current ? (current.show ?? current.a.slice(1)) : [];
  const shown = battling ? getDeck(duel.deckId) : deck;

  return (
    <div className="app">
      <Header
        showSwitch={screen === "solo" || screen === "lobby"}
        showAccount={screen === "solo"}
        showLeave={screen === "battle" || screen === "end"}
        deckId={deckId}
        onDeck={selectDeck}
        user={user}
        onSignIn={() => {
          clearTimeout(advanceTimer.current);
          setScreen("login");
        }}
        onSignOut={() => void onSignOut()}
        canSpeak={canSpeak}
        muted={muted}
        onToggleSound={() => {
          const nextMuted = !muted;
          setMuted(nextMuted);
          store.setStoredMuted(nextMuted);
          if (nextMuted) stopSpeaking();
          // Hand the keyboard back. Everything here is driven by Enter, so
          // leaving focus on a header button means the next keystroke toggles
          // sound again instead of answering.
          focusInput();
        }}
        onBattle={() => {
          clearTimeout(advanceTimer.current);
          clearError();
          setJoinError("");
          setScreen("lobby");
        }}
        onLeave={leaveDuel}
      />

      <main className="main">
        {screen === "solo" && <StatChips counts={stats} streak={streak} />}

        {screen === "battle" && (
          <Scoreboard
            me={{ name: displayName(), score: duel.myScore, done: duel.myFinished ? duel.deck.length : duel.bidx }}
            opponent={{
              name: duel.oppName || "Opponent",
              score: duel.oppScore,
              done: duel.oppDone,
              away: !duel.oppPresent,
            }}
            total={duel.deck.length}
          />
        )}

        {(screen === "solo" || (screen === "battle" && !duel.myFinished)) &&
          (current ? (
            <AnswerCard
              direction={shown.direction}
              word={current.w}
              wordLang={shown.lang}
              answerLang={shown.answerLang}
              answerLabel={shown.answerLabel}
              typed={typed}
              onTyped={setTyped}
              revealed={phase === "revealed"}
              wasCorrect={wasCorrect}
              mainAnswer={current.a[0] ?? ""}
              alts={alts}
              onCheck={check}
              onNext={next}
              onGiveUp={giveUp}
              inputRef={inputRef}
              onSpeak={
                canSpeak && !muted
                  ? () => {
                      speak(current.a[0] ?? "", shown.answerLang);
                      focusInput();
                    }
                  : undefined
              }
            />
          ) : (
            <div className="card card-centered">
              <div className="done-line">This deck is empty</div>
              <p className="subtitle">Pick another deck from the switcher above.</p>
            </div>
          ))}

        {screen === "battle" && duel.myFinished && (
          <div className="card card-centered">
            <div className="done-line">
              You&rsquo;re done — {duel.myScore}/{duel.deck.length}
            </div>
            <Waiting>
              {duel.oppPresent
                ? `Waiting for ${duel.oppName || "your opponent"}…`
                : `${duel.oppName || "Your opponent"} disconnected — holding their place…`}
            </Waiting>
          </div>
        )}

        {screen === "lobby" && (
          <Lobby
            name={name}
            onName={(v) => {
              setName(v);
              store.setStoredName(v);
            }}
            wordCount={wordCount}
            onWordCount={setWordCount}
            joinCode={joinCode}
            onJoinCode={(v) => {
              setJoinCode(v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4));
              setJoinError("");
              clearError();
            }}
            duel={{ ...duel, error: joinError || duel.error }}
            onCreate={startDuel}
            onCancel={leaveDuel}
            onJoin={joinDuel}
            onBack={leaveDuel}
          />
        )}

        {screen === "end" && (
          <DuelResult duel={duel} onRematch={rematch} onBack={leaveDuel} />
        )}

        {screen === "login" && (
          <SignIn onDone={(email) => void onSignedIn(email)} onBack={() => setScreen("solo")} />
        )}
      </main>
    </div>
  );
}
